import React from 'react';
import { AImage } from '../../atoms/AImage';
import { AEyebrowVariant } from '../../atoms/AEyebrow';
import { AScrimGradient } from '../../atoms/AScrimGradient';
import { MTextBlockBasic } from '../../molecules/MTextBlockBasic';
import { AIcon } from '../../atoms/AIcon';
import { AIconSize } from '../../atoms/AIcon/AIcon.types';
import { MTag } from '../../molecules/MTag';
import { MButton } from '../../molecules/MButton';
import { MButtonIcon, MButtonIconState } from '../../molecules/MButtonIcon';
import { MLogoContainer } from '../../molecules/MLogoContainer';
import type { MLogoContainerColor } from '../../molecules/MLogoContainer/MLogoContainer.types';
import * as Styled from './OCardLayeredStandard.styles';
import {
  OCardLayeredStandardProps,
  OCardLayeredStandardCTAStyle,
  OCardLayeredStandardAspectRatio,
} from './OCardLayeredStandard.types';
import {
  getEllipsisClassName,
  getImageRenditionsForAspectRatio,
  getResponsiveAspectRatioConfig,
  getTextClampConfig,
  resolveAspectRatio,
  shouldShowLogo,
} from './OCardLayeredStandard.utils';
import clsx from 'clsx';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OCardLayeredStandard';
const DATA_TESTID = 'web-ui-kit-OCardLayeredStandard';
const ARROW_CTA_ICON = 'icon-arrow-right';

const resolveCtaStyle = (ctaStyle?: OCardLayeredStandardCTAStyle | string): OCardLayeredStandardCTAStyle => {
  if (ctaStyle === OCardLayeredStandardCTAStyle.Arrow || ctaStyle === 'Arrow') {
    return OCardLayeredStandardCTAStyle.Arrow;
  }
  return OCardLayeredStandardCTAStyle.Button;
};

const shouldShowButtonIcon = (
  buttonIconConfig?: { aIcon?: { icon?: string } } | null,
  ctaStyle?: OCardLayeredStandardCTAStyle | string
): boolean => Boolean(ctaStyle === OCardLayeredStandardCTAStyle.Button && buttonIconConfig?.aIcon?.icon?.trim());

export const OCardLayeredStandard: React.FC<OCardLayeredStandardProps> = ({
  ctaLink,
  aspectRatio = OCardLayeredStandardAspectRatio.WIDE_16_9,
  ctaStyle: ctaStyleProp = OCardLayeredStandardCTAStyle.Button,
  aImage,
  aScrimGradient,
  mLogoContainer,
  aLogo32,
  mTextBlockBasic,
  aIcon,
  mTag,
  mButton,
  mButton2,
  mButtonIcon,
  trackingProperties,
  className,
}) => {
  const resolvedAspectRatio = resolveAspectRatio(aspectRatio);
  const { mobile: mobileAspectRatio, desktop: desktopAspectRatio } =
    getResponsiveAspectRatioConfig(resolvedAspectRatio);
  const { eyebrowLines, headlineLines, bodyLines } = getTextClampConfig(resolvedAspectRatio);

  const ctaStyle = resolveCtaStyle(ctaStyleProp);
  const isArrowCta = ctaStyle === OCardLayeredStandardCTAStyle.Arrow;
  const isButtonCta = ctaStyle === OCardLayeredStandardCTAStyle.Button;

  const imageRenditions =
    aImage?.image?.assetPath && aImage.image.dynamic !== false
      ? getImageRenditionsForAspectRatio(resolvedAspectRatio, aImage.image.assetPath)
      : [];

  const showButton1 = isButtonCta && Boolean(mButton?.buttonText);
  const showButton2 = isButtonCta && Boolean(mButton2?.buttonText);
  const showArrowIcon = isArrowCta;
  const showTopIcon = isButtonCta && Boolean(aIcon);

  const logoConfig = mLogoContainer?.aLogo32 ?? aLogo32;
  const showLogo = shouldShowLogo(resolvedAspectRatio, logoConfig);
  const showButtonIcon = shouldShowButtonIcon(mButtonIcon, ctaStyle);

  const handleCardClick = () => {
    if (isArrowCta && ctaLink) {
      window.location.href = ctaLink;
    }
  };

  const isClickable = isArrowCta && Boolean(ctaLink);
  const cardAriaLabel = mTextBlockBasic?.aSubheader?.text || mTextBlockBasic?.aEyebrow?.eyebrowText || undefined;

  return (
    <Styled.Wrapper
      className={`${className || ''} inverse`}
      data-component-name={COMPONENT_NAME}
      data-testid={DATA_TESTID}
      $isClickable={isClickable}
      $mobileAspectRatio={mobileAspectRatio}
      $desktopAspectRatio={desktopAspectRatio}
      onClick={isClickable ? handleCardClick : undefined}
      role={isClickable ? 'link' : undefined}
      aria-label={isClickable ? cardAriaLabel : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCardClick();
              }
            }
          : undefined
      }
    >
      <Styled.ImageContainer data-aspect-ratio={resolvedAspectRatio}>
        {aImage?.image && (
          <AImage
            image={aImage.image}
            alt={aImage.image.altText || ''}
            loading="lazy"
            imgClass="o-card-layered-standard-image"
            renditions={imageRenditions}
          />
        )}
      </Styled.ImageContainer>

      {mTag?.aLabel?.text && (
        <Styled.TagOverlay data-testid={`${DATA_TESTID}-tag`}>
          <MTag
            type={mTag.type}
            aLabel={{
              text: mTag.aLabel.text,
              size: mTag.aLabel.size,
            }}
            aIcon={mTag.aIcon}
          />
        </Styled.TagOverlay>
      )}

      {showButtonIcon && mButtonIcon?.aIcon?.icon && (
        <Styled.ButtonIconOverlay data-testid={`${DATA_TESTID}-button-icon`}>
          <MButtonIcon
            aIcon={{ icon: mButtonIcon.aIcon.icon }}
            state={mButtonIcon.state ?? MButtonIconState.Transparent}
            aria-label={
              mButtonIcon['aria-label'] ||
              mTextBlockBasic?.aSubheader?.text ||
              mTextBlockBasic?.aEyebrow?.eyebrowText ||
              'Open'
            }
            onClick={e => {
              e.stopPropagation();
              mButtonIcon.onClick?.(e);
            }}
            trackingProperties={trackingProperties}
          />
        </Styled.ButtonIconOverlay>
      )}

      <Styled.ContentSafeZone $hasLogo={showLogo}>
        {aScrimGradient && (
          <Styled.ScrimWrapper>
            <AScrimGradient direction={aScrimGradient.direction} />
          </Styled.ScrimWrapper>
        )}

        {showLogo && logoConfig && (
          <Styled.LogoWrapper data-testid={`${DATA_TESTID}-logo`}>
            <MLogoContainer
              brand={logoConfig.brand?.trim() || ''}
              aspectRatio="3:2"
              width={78}
              height={52}
              color={(logoConfig.color as MLogoContainerColor | undefined) || 'white'}
              logopath={logoConfig.logopath}
            />
          </Styled.LogoWrapper>
        )}

        {showTopIcon && aIcon && (
          <Styled.TopIconWrapper>
            <AIcon icon={aIcon.icon} size={aIcon.size} />
          </Styled.TopIconWrapper>
        )}

        {(mTextBlockBasic || showArrowIcon) && (
          <Styled.TextRow>
            {mTextBlockBasic && (
              <Styled.TextBlockWrapper>
                <MTextBlockBasic
                  alignment={mTextBlockBasic.alignment}
                  aEyebrow={
                    mTextBlockBasic.aEyebrow?.eyebrowText
                      ? {
                          ...mTextBlockBasic.aEyebrow,
                          variant: AEyebrowVariant.Secondary,
                          className: clsx(getEllipsisClassName(eyebrowLines)),
                        }
                      : undefined
                  }
                  aSubheader={
                    mTextBlockBasic.aSubheader?.text
                      ? {
                          text: mTextBlockBasic.aSubheader.text,
                          size: mTextBlockBasic.aSubheader.size,
                          element: mTextBlockBasic.aSubheader.element,
                          className: getEllipsisClassName(headlineLines),
                        }
                      : undefined
                  }
                  aBody={
                    mTextBlockBasic.aBody?.bodyText
                      ? {
                          bodyText: mTextBlockBasic.aBody.bodyText,
                          size: mTextBlockBasic.aBody.size,
                          color: mTextBlockBasic.aBody.color,
                          className: getEllipsisClassName(bodyLines),
                        }
                      : undefined
                  }
                />
              </Styled.TextBlockWrapper>
            )}

            {showArrowIcon && (
              <Styled.ArrowIconWrapper data-testid={`${DATA_TESTID}-arrow-icon`} aria-hidden="true">
                <AIcon icon={ARROW_CTA_ICON} size={AIconSize.L} />
              </Styled.ArrowIconWrapper>
            )}
          </Styled.TextRow>
        )}

        {(showButton1 || showButton2) && (
          <Styled.ButtonContainer>
            {showButton1 && mButton && (
              <Styled.ButtonWrapper>
                <MButton
                  buttonType={mButton.type}
                  size={mButton.size}
                  ctaLink={mButton.ctaLink || ctaLink}
                  openInANewTab={mButton.openInANewTab}
                  iconPlacement={mButton.icon}
                  aIcon={mButton.aIcon}
                  trackingProperties={trackingProperties}
                >
                  {mButton.buttonText}
                </MButton>
              </Styled.ButtonWrapper>
            )}

            {showButton2 && mButton2 && (
              <Styled.ButtonWrapper>
                <MButton
                  buttonType={mButton2.type}
                  size={mButton2.size}
                  ctaLink={mButton2.ctaLink}
                  openInANewTab={mButton2.openInANewTab}
                  iconPlacement={mButton2.icon}
                  aIcon={mButton2.aIcon}
                  trackingProperties={trackingProperties}
                >
                  {mButton2.buttonText}
                </MButton>
              </Styled.ButtonWrapper>
            )}
          </Styled.ButtonContainer>
        )}
      </Styled.ContentSafeZone>
    </Styled.Wrapper>
  );
};

export default OCardLayeredStandard;
